import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { UserRole, UserStatus } from '../../types';
import MemberDashboard from './MemberDashboard';
import AdminDashboard from './AdminDashboard';
import SuperAdminDashboard from './SuperAdminDashboard';
import CoachDashboard from './CoachDashboard';
import { Box, Container, Typography, Paper, Button, CircularProgress } from '@mui/material';
import { memberProfileAPI } from '../../services/api';

const Dashboard: React.FC = () => {
  const { user, isAuthenticated, loading, selectedMemberProfile, setSelectedMemberProfile } = useAuth();
  const [memberProfileCheck, setMemberProfileCheck] = useState<'loading' | 'complete' | 'redirect'>('loading');

  // Check member profile selection for member users
  useEffect(() => {
    const checkMemberProfiles = async () => {
      // Only check for member users
      if (user?.role !== UserRole.MEMBER) {
        setMemberProfileCheck('complete');
        return;
      }

      // If already have a selected member profile, no need to check
      if (selectedMemberProfile) {
        setMemberProfileCheck('complete');
        return;
      }

      try {
        const response = await memberProfileAPI.getUserMemberProfiles(user.id);
        const allProfiles = response.data || [];
        
        // Filter to only active, pending, or on_hold profiles
        const allowedStatuses = ['active', 'pending', 'on_hold', 'suspended'];
        const profiles = allProfiles.filter((profile: any) => 
          allowedStatuses.includes(profile.status.toLowerCase())
        );

        if (profiles.length === 0) {
          // No active member profiles found, proceed to dashboard with error state
          setMemberProfileCheck('complete');
        } else if (profiles.length === 1) {
          // Auto-select single active/pending/on_hold profile
          try {
            // Record profile access on backend
            await memberProfileAPI.recordProfileAccess(profiles[0].id);
          } catch (error) {
            console.error('Error recording profile access:', error);
            // Don't block auto-selection if recording fails
          }
          setSelectedMemberProfile(profiles[0]);
          localStorage.setItem('selectedMemberProfile', JSON.stringify(profiles[0]));
          setMemberProfileCheck('complete');
        } else {
          // Multiple active/pending/on_hold profiles, need to redirect to selection page
          setMemberProfileCheck('redirect');
        }
      } catch (error) {
        console.error('Error fetching member profiles:', error);
        setMemberProfileCheck('complete');
      }
    };

    if (user && isAuthenticated && !loading) {
      checkMemberProfiles();
    }
  }, [user, isAuthenticated, loading, selectedMemberProfile, setSelectedMemberProfile]);

  if (loading || memberProfileCheck === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" />;
  }

  // Redirect member users to profile selection if needed
  if (memberProfileCheck === 'redirect') {
    return <Navigate to="/select-member-profile" />;
  }

  // Show a special screen for pending users
  if (user.status === UserStatus.PENDING) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 8, textAlign: 'center' }}>
          <Typography variant="h5" color="primary" gutterBottom>
            Membership Pending Approval
          </Typography>
          <Typography variant="body1" paragraph>
            Thank you for registering, {user.firstName}. Your membership application is currently being reviewed.
          </Typography>
          <Typography variant="body1" paragraph>
            You'll receive an email notification once your application has been approved.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Application submitted on: {new Date(user.createdAt).toLocaleDateString()}
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Show a special screen for suspended users
  if (user.status === UserStatus.SUSPENDED) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 8, textAlign: 'center', bgcolor: 'error.light' }}>
          <Typography variant="h5" color="error" gutterBottom>
            Account Suspended
          </Typography>
          <Typography variant="body1" paragraph>
            Your account has been suspended. Please contact club management for more information.
          </Typography>
          <Button variant="contained" color="primary" sx={{ mt: 2 }}>
            Contact Support
          </Button>
        </Paper>
      </Container>
    );
  }

  // Show a special screen for membership on hold
  if (user.status === UserStatus.ON_HOLD) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 8, textAlign: 'center', bgcolor: 'warning.light' }}>
          <Typography variant="h5" color="warning.dark" gutterBottom>
            Membership On Hold
          </Typography>
          <Typography variant="body1" paragraph>
            Your membership is currently on hold per your request.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Hold Start Date: {user.membershipHoldStartDate ? new Date(user.membershipHoldStartDate).toLocaleDateString() : 'N/A'}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Hold End Date: {user.membershipHoldEndDate ? new Date(user.membershipHoldEndDate).toLocaleDateString() : 'N/A'}
          </Typography>
          <Button variant="contained" color="primary" sx={{ mt: 2 }}>
            Reactivate Membership
          </Button>
        </Paper>
      </Container>
    );
  }  // Handle redirect to members page if the URL has members in it
  // This is a way to support the navigation even though we're in the Dashboard component
  if (window.location.pathname === '/members' && (user.role === UserRole.ADMIN || user.role === UserRole.EMPLOYEE)) {
    return <Navigate to="/members" />;
  }
  
  // Render appropriate dashboard based on user role
  if (user.role === UserRole.SUPER_ADMIN) {
    // If the super admin has selected a specific tenant to manage
    const selectedTenantId = localStorage.getItem('tenantId');
    if (selectedTenantId) {
      // When super admin is viewing a specific tenant, they should see that tenant's admin dashboard
      return <AdminDashboard />;
    }
    // Show super admin dashboard with tenant management
    return <SuperAdminDashboard />;
  } else if (user.role === UserRole.ADMIN) {
    return <AdminDashboard />;
  } else if (user.role === UserRole.EMPLOYEE) {
    // Employee would have a separate dashboard that we could build later
    return <AdminDashboard />;
  } else if (user.role === UserRole.COACH) {
    return <CoachDashboard />;
  } else {
    return <MemberDashboard />;
  }
};

export default Dashboard;
